import { useContext, useEffect, useState } from "react";
import "./Products.css";

//conifg imports
import SearchBy from "../SearchBy/SearchBy";
import { productSearchByLookup } from "./Products_config";

//JSX components imports
import InputSearch from "../InputSearch/InputSearch";

import { getLoggedInUser } from "../../../utils/authUtils";

//AntDesign Imports
import { Checkbox, Spin } from "antd";

//FontAwesome Imports
import { getInventory } from "../../../services/inventoryService";
import { Store } from "../../../store";
import { eventEmiterInstance } from "../../../utils/EventEmitter";
import { highlightLocations, uuid } from "../../../utils/helpers";
import InventoryItem from "./InventoryItem/InventoryItem";

const INITIAL_MESSAGE = 'Enter search term and click "GO" to find inventory.';

const Products = ({ vref, isActive }) => {
  // --- States ---
  const {
    state: { elements },
    dispatch,
  } = useContext(Store);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [inputSearch, setInputSearch] = useState(null);
  const [inventoryList, setInventoryList] = useState(null);
  const [loading, setLoading] = useState(false);
  let message = null;
  let selectedItemsMap = new Map();

  const user = getLoggedInUser();
  const hasInventory = inventoryList && inventoryList.length > 0;

  // derive highlights from map
  const deriveHighlights = (selectedMap) => [...selectedMap.values()];

  const buildSelectedItemsMap = (inventory) => new Map(inventory.filter((item) => item.isSelected).map((item) => [item.tempId, item.doc_location]));

  const applySelection = (updatedInventory) => {
    setInventoryList(updatedInventory);

    const selectedMap = buildSelectedItemsMap(updatedInventory);
    selectedItemsMap = selectedMap;

    handleHighlightLocations(deriveHighlights(selectedMap));
  };

  const clearSelection = () => {
    const clearedInventory = inventoryList.map((item) => ({
      ...item,
      isSelected: false,
    }));

    selectedItemsMap = new Map();
    setInventoryList(clearedInventory);

    eventEmiterInstance.emit("resetAllRacks", {});
    eventEmiterInstance.emit("hideRacks", null);

    handleHighlightLocations([]);
  };

  const onCheckboxHandler = ({ target: { checked } }) => {
    if (checked) {
      onSelectAllInventory();
    } else {
      clearSelection();
    }
  };

  const onItemSelect = (object) => {
    if (!object?.tempId || !Array.isArray(inventoryList)) return;

    const selectedTempId = object.tempId;

    const isAlreadySelected = inventoryList.some((item) => item.tempId === selectedTempId && item.isSelected);

    const updatedInventory = inventoryList.map((item) => (item.tempId === selectedTempId ? { ...item, isSelected: !isAlreadySelected } : item));

    applySelection(updatedInventory);
  };

  const onSelectAllInventory = () => {
    const updatedInventory = inventoryList.map((item) => {
      const i = {
        ...item,
        isSelected: true,
      };
      selectedItemsMap.set(i.tempId, i.doc_location);
      return i;
    });

    applySelection(updatedInventory);
  };

  // Determines the final inventory list to use, potentially filtering bins as a fallback for docLocation search.
  const resolveInventoryList = (searchBy, searchTerm, bins = [], currentInventory = []) => {
    // Return inventoryList for docLocation otherwise, filter bins by inputSearch.
    // if (searchBy === "docLocation") {
    //   //Filter bins by search term if inventory is empty.
    //   return currentInventory?.length ? currentInventory.map((item, i) => ({ ...item, index: i, isSelected: false, tempId: uuid() })) : [];
    // }
    return currentInventory?.map((item, i) => ({ ...item, index: i, isSelected: false, tempId: uuid() })) || [];
  };

  const onSearchInventoryHandler = async () => {
    // input search is empty then clear the inventory list and return
    if (inputSearch?.length === 0) {
      setInventoryList(null);
      message = INITIAL_MESSAGE;
      eventEmiterInstance.emit("hideRacks", null);
      eventEmiterInstance.emit("resetAllRacks", {});
      return;
    }

    if (selectedProduct === null) return;
    setLoading(true);
    message = null;
    const payload = {
      warehouseCode: user.warehouseCode,
      searchBy: selectedProduct.name,
      searchTerm: inputSearch,
      productName: selectedProduct.name,
    };
    try {
      const { inventoryList, warehouseStorageLocations } = await getInventory(payload);
      const finalInventory = resolveInventoryList(selectedProduct.name, inputSearch, warehouseStorageLocations, inventoryList);
      // Build a lookup from doc_location -> tags
      const tagsByLocation = new Map(finalInventory.map((item) => [item.doc_location, Array.isArray(item.tags) ? item.tags : []]));

      const ee = elements?.map((element) => {
        if (element?.objectType === "rack") {
          return {
            ...element,
            bins: element?.bins?.map((bin) => {
              const key = bin.code ?? bin.name; // return code because code is stored in doc_location and not name
              const tags = tagsByLocation.get(key) ?? [];
              return { ...bin, tags };
            }),
          };
        } else if (element?.objectType === "area") {
          if (element?.subAreas?.length === 0) {
            const key = element?.code ?? element?.name; // adjust if doc_location maps to name instead
            const tags = tagsByLocation.get(key) ?? [];
            return {
              ...element,
              tags,
            };
          }
          return {
            ...element,
            subAreas: element?.subAreas?.map((bin) => {
              const key = bin.code ?? bin.name; // adjust if doc_location maps to name instead
              const tags = tagsByLocation.get(key) ?? [];
              return { ...bin, tags };
            }),
          };
        }
        return element;
      });

      dispatch({
        type: "BATCH_DISPATCH",
        payload: [
          { type: "SET_ELEMENTS", payload: ee },
          { type: "SET_INITIAL_LOAD", payload: true },
        ],
      });
      setInventoryList(finalInventory);
      eventEmiterInstance.emit("hideRacks", null);

      if (finalInventory.length === 0) {
        message = `No Records Found for ${inputSearch}`;
        handleHighlightLocations([]);
      } else {
        message = null;
      }
    } catch (error) {
      console.log("Error fetching inventory", error);
      message = "Error fetching inventory";
    } finally {
      setLoading(false);
    }
  };

  const handleHighlightLocations = (locations) => {
    console.log(locations);
    if (locations && locations.length > 0) {
      highlightLocations(locations);
    } else {
      // Clear all highlights when locations is empty
      eventEmiterInstance.emit("hideRacks", null);
      eventEmiterInstance.emit("resetAllRacks", {});
    }
  };

  const onChangeInputHandler = (searchValue) => {
    if (!searchValue) {
      setInputSearch(null);
      setInventoryList(null);
      message = null;
      eventEmiterInstance.emit("hideRacks", null);
      eventEmiterInstance.emit("resetAllRacks", {});
      return;
    }
    setInputSearch(searchValue);
  };

  const InputSuffix = () => {
    return (
      <div className="searchIcon" onClick={onSearchInventoryHandler}>
        GO
      </div>
    );
  };

  const handleSelectProduct = (product) => {
    if (!product) {
      setSelectedProduct(null);
      setInputSearch(null);
      setInventoryList(null);
      eventEmiterInstance.emit("resetAllRacks", {});
      return;
    }

    if (product && selectedProduct?.name !== product?.name) {
      setSelectedProduct(product);
      setInputSearch(null);
      setInventoryList(null);
    }
  };

  useEffect(() => {
    //if isActive is false then reset the state
    if (!isActive) {
      setSelectedProduct(null);
      setInventoryList(null);
      setInputSearch(null);
    }
    return () => {
      setSelectedProduct(null);
      setInventoryList(null);
      setInputSearch(null);
    };
  }, [isActive]);

  const selectedItemsCount = inventoryList?.filter((item) => item?.isSelected)?.length || 0;

  const getDisplayMessage = () => {
    if (message) return message;

    if (inputSearch && inventoryList?.length === 0) {
      return `No Records Found for "${inputSearch}"`;
    }

    if (selectedProduct) {
      return `Enter ${selectedProduct.value}`;
    }

    if (inputSearch && inventoryList?.length > 0) {
      return null;
    }

    return "Select Search By";
  };

  const displayMessage = getDisplayMessage();

  return (
    <div className="products-container">
      <SearchBy
        searchTitle={"Search By:"}
        elementSize={170}
        options={productSearchByLookup}
        value={selectedProduct}
        handleSelectProduct={handleSelectProduct}
      />
      <InputSearch
        clearInput={true}
        restrictSpace={false}
        addonAfterElement={<InputSuffix />}
        inputSearch={inputSearch}
        setValue={setInputSearch}
        onChangeInputHandler={onChangeInputHandler}
        placeholderText={selectedProduct?.value ? `Enter ${selectedProduct?.value}` : "Select search By"}
        isDisabled={selectedProduct?.value ? false : true}
      />

      {/* Loading Indicator */}
      {loading && (
        <div className="loading-overlay">
          <Spin size="medium" />{" "}
        </div>
      )}

      {/* "Select All" Checkbox - Show only when results exist and not loading */}
      {!loading && hasInventory && (
        <div className="products-selectAll">
          <Checkbox
            indeterminate={selectedItemsCount > 0 && selectedItemsCount < inventoryList.length}
            checked={selectedItemsCount === inventoryList.length}
            onChange={onCheckboxHandler}
          >
            Select All
          </Checkbox>
          {selectedItemsCount > 0 && <span className="selected-count">({selectedItemsCount} selected)</span>}
        </div>
      )}

      <div className="productResults">
        {/* Inventory Items List Start */}
        <div className="inventory-items-list">
          {!loading &&
            hasInventory &&
            inventoryList.map((item, index) => {
              return (
                <InventoryItem
                  key={`${index}-${item.doc_location}`}
                  item={item}
                  selectedProduct={selectedProduct}
                  onItemSelect={onItemSelect}
                  isSelected={item.isSelected}
                />
              );
            })}

          {displayMessage?.length && !hasInventory && <div className="message-info">{displayMessage}</div>}
        </div>
      </div>
    </div>
  );
};

export default Products;
